import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DataIntervalTagEditDialog } from '@app/shared/edit-dialog/data-interval-tag/data-interval-tag-edit-dialog.component';
import { WebClient } from '@injectables';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { DataIntervalTag } from '../../../../../../shared-models/public/data-interval-tag';
import { combineLatest, op, startWith, Subject, Subscription, switchMap } from '@lib/rxjs';
import { DateTime } from '@lib/date-time';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { Client, User } from '@models';
import { MeasurementTypeToUnit } from '@lib/angular/measurement/measurement-type-to-unit';
import { addSelectFilterConfig, ModalFilter } from '@app/shared/model-table/model-table-filter';
import { ModelTableComponent } from '@app/shared/model-table/model-table.component';
import { UnitSystem } from '@lib/measurement/types';

@Component({
    selector: 'live-tag-settings',
    templateUrl: './live-tag-settings.component.html',
    styleUrls: ['./live-tag-settings.component.scss'],
})
export class LiveTagSettingsComponent implements OnInit, OnDestroy {
    constructor(
        public readonly user:User,
        private readonly webClient: WebClient,
        private readonly fragQuery: FragQueryService,
        private readonly router: Router,
        private readonly dialog: DialogService,
        private readonly measurementTypeToUnit:MeasurementTypeToUnit,
        
    ) {
        combineLatest([
            this.fragQuery.client$,
            this.fragQuery.particleSizeUnitSystem$
        ]).subscribe(([client, unitSystem]) => {
            this.initializeAsyncFilters(client, unitSystem);
        });
    }
    
    @ViewChild(ModelTableComponent)
    private modelTable: ModelTableComponent<DataIntervalTag, WebClient.RestTypes>;
    
    private subscriptions: Subscription = new Subscription();
    public readonly quantityUnitSystem$ = this.fragQuery.QuantityUnitSystem$;
    public readonly devices$ = this.fragQuery.devices$;
    public readonly device$ = this.fragQuery.device$;
    public readonly deviceUuid$ = this.fragQuery.deviceUuid$;
    public materialTypesArray: { text: string; color: string }[] = [];
    public readonly particleSizeUnitSystem$ = this.fragQuery.particleSizeUnitSystem$;
    public readonly model = DataIntervalTag;
    public readonly restClient = this.webClient.model.dataIntervalTag;
    public readonly editDialog = DataIntervalTagEditDialog;
    private refreshTrigger$ = new Subject<void>();
    public readonly where$ = this.refreshTrigger$.pipe(
        startWith(null),
        switchMap(() => this.device$.pipe(
            op.map((device) => {
                if (!device) {
                    return null;
                }
                return { deviceId: device.id };
            })
        ))
    );

    public ngOnInit(): void {
        this.subscriptions.add(this.fragQuery.userSettingUpdated$.subscribe(() => this.refresh()));
    }

    public ngOnDestroy(): void {
        this.subscriptions?.unsubscribe();
    }
    
    public updateRecords() {
        this.refreshTrigger$.next();
    }

    public refresh() {
        this.modelTable?.updateRecords();
    }
    
    private initializeAsyncFilters(client: Client, unitSystem: UnitSystem): void {
        const typeToUnit = this.measurementTypeToUnit.getUnitsForSystem(unitSystem);
        
        this.materialTypesArray = client.materialType ? JSON.parse(client.materialType) : [];
        if (this.materialTypesArray?.length) {
            this.filterConfigs.push(addSelectFilterConfig<string>("Material Type", "materialType", false,this.materialTypesArray.map(m => m.text)));
        }else{
            this.filterConfigs.push({
                type: 'input-string',
                column: 'Material Type',
                dbColumn: "materialType",
            })
        }
    }

    public async openLiveTagDialog() {
        const tagged = await this.fragQuery.openLiveTagDialog();
        if (tagged) {
            this.updateRecords()
        }
    }

    public openInDTimeline(tag: DataIntervalTag) {
        let url = '/analytics#';
        const timeRange = [new DateTime(1000 * tag.begin)];
        if (tag.end) {
            timeRange.push(new DateTime(1000 * tag.end));
        }
        url += JSON.stringify({ custom: timeRange });
        this.router.navigateByUrl(url);
    }

    public onAdd(resolve:(confirm:Promise<boolean>)=>void){
        resolve(this.fragQuery.openLiveTagDialog(true, true));
    }

    public onOpenLiveTagDefaultConfigsDialog(){
        this.dialog.openLiveTagDefaultConfigsDialog()
    }

    public openAddMaterialType() {
        this.dialog.openAddMaterialType()
    }

    public openAddBenchHeightConfig() {
        this.dialog.openAddBenchHeightConfigDialog()
    }

    public openAddBenchSeamConfig() {
        this.dialog.openAddBenchSeamConfigDialog()
    }

    public getMaterialTypeColor(materialType: string): string {
        const material = this.materialTypesArray.find(item => item.text === materialType);
        return material ? material.color : 'transparent';
    }

    public filterConfigs: ModalFilter[] = [
        {
            type: 'input-number',
            column: 'Truck Load Count',
            dbColumn: "truckLoadCount",
        },
        {
            type: 'input-string',
            column: 'Comments',
            dbColumn: "comments",
        },
        {
            type: 'date',
            column: 'Begin',
            dbColumn: "begin",
        },
        {
            type: 'date',
            column: 'End',
            dbColumn: "end",
        },
        {
            type: "measurement-particleSize",
            column: 'Tolerance Spec',
            dbColumn: "toleranceSpecSize",
            unitSystem$: this.fragQuery.particleSizeUnitSystem$
        },
        {
            type: "measurement-distance",
            column: 'Bench Height',
            dbColumn: "benchHeightSize",
            unitSystem$: this.fragQuery.particleSizeUnitSystem$
        },
        {
            type: "measurement-distance",
            column: 'Bench Seam',
            dbColumn: "benchSeamSize",
            unitSystem$: this.fragQuery.particleSizeUnitSystem$
        }
    ];
    
}